import React from "react";
import PlaylistMosaic from "./PlaylistMosaic";
import PopupMenu from "./PopupMenu";
import logo from "../Assets/logo.png";

/**
 * PlaylistCard - Vertical card component for displaying playlists
 * Optimized for grid layouts on both mobile and desktop
 */
export default function PlaylistCard({
  title,
  image,
  thumbnails,
  onSelect,
  onPlay,
  onEdit,
  onShare,
  onDelete,
  action, // Keep for backward compatibility
  // Sharing indicators
  isShared = false, // Is this playlist shared with the current user
  sharedBy = null, // Username of the owner (for shared playlists)
  shareStatus = 'private', // 'private', 'specific', or 'everyone'
  accessLevel = null, // 'play' or 'manage' (for shared playlists)
}) {
  return (
    <div className="group relative bg-secondary border border-neutral-800 rounded-md overflow-hidden transition-all duration-200 ease-out hover:border-neutral-700 hover:shadow-lg cursor-pointer animate-scale-in">
      {/* Main clickable area */}
      <div onClick={onSelect}>
        {/* Image Container - Square aspect ratio */}
        <div className="relative w-full pt-[100%] bg-neutral-900 overflow-hidden">
          {/* Use mosaic if thumbnails provided, otherwise use image prop */}
          {thumbnails ? (
            <PlaylistMosaic thumbnails={thumbnails} />
          ) : (
            <img
              src={image || logo}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Share Status Icon - Top left corner overlay */}
          <div className="absolute top-2 left-2 z-10">
            {shareStatus === 'private' && !isShared && (
              <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-full p-1.5" title="Private">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C9.243 2 7 4.243 7 7v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7c0-2.757-2.243-5-5-5zM9 7c0-1.654 1.346-3 3-3s3 1.346 3 3v3H9V7z" />
                </svg>
              </div>
            )}
            {shareStatus === 'specific' && !isShared && (
              <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-full p-1.5" title="Shared with specific users">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </div>
            )}
            {shareStatus === 'everyone' && !isShared && (
              <div className="bg-black bg-opacity-70 backdrop-blur-sm rounded-full p-1.5" title="Shared with all friends">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
              </div>
            )}
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300" />
        </div>

        {/* Content */}
        <div className="p-3 md:p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-sm md:text-base font-semibold text-text truncate flex-1">
              {title}
            </h3>

            {/* Quick Play Button */}
            {onPlay && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlay();
                }}
                className="
                  flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-accent hover:bg-accent/20 hover:scale-105 transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-accent
                "
                aria-label="Play playlist"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
          </div>

          {/* Badges for shared playlists */}
          {isShared && sharedBy && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 bg-opacity-20 text-blue-400">
                Shared by @{sharedBy}
              </span>
              {accessLevel === 'play' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500 bg-opacity-20 text-gray-400">
                  View only
                </span>
              )}
              {accessLevel === 'manage' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent bg-opacity-20 text-accent">
                  Can manage
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Popup Menu - Shows on hover (desktop) or always (mobile) */}
      {(onEdit || onShare || onDelete) && (
        <div className="absolute top-2 right-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200">
          <PopupMenu
            trigger={
              <button
                className="
                  w-8 h-8 rounded-full bg-black bg-opacity-70 hover:bg-opacity-90 backdrop-blur-sm flex items-center justify-center text-white transition-all focus:outline-none focus:ring-2 focus:ring-accent
                "
                aria-label="Playlist options"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
            }
            items={[
              onEdit && {
                label: 'Edit',
                icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                onClick: (e) => {
                  if (e) e.stopPropagation();
                  onEdit();
                },
                variant: 'default',
              },
              onShare && {
                label: 'Share',
                icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                ),
                onClick: (e) => {
                  if (e) e.stopPropagation();
                  onShare();
                },
                variant: 'default',
              },
              onDelete && {
                label: 'Delete',
                icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                ),
                onClick: (e) => {
                  if (e) e.stopPropagation();
                  onDelete();
                },
                variant: 'danger',
              },
            ].filter(Boolean)}
            position="bottom-right"
          />
        </div>
      )}

      {/* Legacy action button support - Shows on hover */}
      {action && !onEdit && !onDelete && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full p-2 backdrop-blur-sm">
            {action}
          </div>
        </div>
      )}
    </div>
  );
}
